import type {
  GradeKey, ParameterDirection, ParameterGroup, ParameterRanges, ParameterValueMap,
  SweetSpotInputPoint, SweetSpotParameter, SweetSpotParameterConfig, SweetSpotPointResult,
} from './types';
import {
  buildParameterRanges, clamp01, isFiniteNumber, normalizeMinMax, normalizeTargetRange,
  normalizeWeights,
} from './sweetSpotUtils';

export type EvaluationMethodId = 'legacy-rule' | 'expert-rule' | 'rq-cq-dual';
export type EvaluationMethodKey = `${EvaluationMethodId}@1.0.0`;
export type EvaluationScope = 'relative' | 'absolute';
export type CombinationStrategy = 'arithmetic_mean' | 'geometric_mean';
export type WeightStrategy = 'fixed' | 'user' | 'ahp';
export type MissingStrategy = 'legacy_group_fallback' | 'strict_dual' | 'available_indicator_reweight';
export type EvaluationMethodDocumentation = {
  displayName: string;
  shortName: string;
  origin: 'legacy' | 'new';
  badges: string[];
  summary: string;
  principle: string;
  scoreNature: 'relative' | 'absolute' | 'config-dependent';
  formulaSections: Array<{
    title: string;
    expression: string;
    explanation: string;
    variables?: string[];
  }>;
  indicatorGroups: Array<{
    group: 'RQ' | 'CQ';
    indicators: string[];
    source: string;
  }>;
  weightingDescription: string;
  aggregationDescription: string;
  missingDataDescription: string;
  gradingDescription: string;
  dataRequirements: string[];
  advantages: string[];
  limitations: string[];
  recommendedFor: string[];
  notRecommendedFor: string[];
  differencesFromLegacy?: string[];
  references: Array<{
    label: string;
    description: string;
    url?: string;
  }>;
  comparison: {
    identity: string;
    scoreNature: string;
    normalization: string;
    weighting: string;
    brittlenessPlacement: string;
    aggregation: string;
    singleGroupMissing: string;
    crossWellComparison: string;
    mainAdvantage: string;
    mainLimitation: string;
  };
};

export type GradeThresholds = { classI: number; classII: number; classIII: number; classIV: number };
export const DEFAULT_GRADE_THRESHOLDS: GradeThresholds = {
  classI: 0.8, classII: 0.6, classIII: 0.4, classIV: 0.2,
};

export type AnchorScoringType =
  | 'positive_linear'
  | 'negative_linear'
  | 'target_range'
  | 'piecewise_linear';

export type PiecewiseAnchor = { value: number; score: number };

export type PhysicalAnchor = {
  parameterId: string;
  scoringType: AnchorScoringType;
  confirmed: boolean;
  source?: string;
  badValue?: number;
  goodValue?: number;
  targetMin?: number;
  targetMax?: number;
  outerMin?: number;
  outerMax?: number;
  breakpoints?: PiecewiseAnchor[];
};

export type AHPResult = {
  matrix: number[][];
  weights: number[];
  lambdaMax: number;
  ci: number;
  cr: number;
  consistent: boolean;
  warnings: string[];
};

export type EvaluationMethodConfig = {
  parameters: SweetSpotParameterConfig;
  anchors: Record<string, PhysicalAnchor | undefined>;
  weightStrategy: WeightStrategy;
  /** 自定义权重快照用于在推荐权重、AHP 权重之间切换时保留用户输入。 */
  customWeights?: Record<string, number>;
  ahp?: Partial<Record<ParameterGroup, AHPResult>>;
  combination: CombinationStrategy;
  gradeThresholds: GradeThresholds;
  allowLegacySingleGroupActivation: boolean;
  /**
   * 储层类型与模板版本（可选，向后兼容）。
   * 旧数据没有这些字段时使用旧版默认行为，不自动迁移、不覆盖旧缓存。
   */
  reservoirType?: import('./types').ReservoirType;
  templateVersion?: string;
  /** 人工审核与确认状态（可选，向后兼容）。 */
  reviewState?: 'pending' | 'reviewed' | 'confirmed';
  reviewComment?: string;
};

export type EvaluationMethodDefinition = {
  methodId: EvaluationMethodId;
  version: '1.0.0';
  definitionHash: string;
  name: string;
  scope: EvaluationScope;
  scopeLabel: string;
  indicatorDefinitions: SweetSpotParameterConfig;
  scoringStrategy: string;
  weightStrategy: string;
  combinationStrategy: string;
  gradingStrategy: string;
  requiredIndicators: string[];
  optionalIndicators: string[];
  missingStrategy: MissingStrategy;
  applicability: string;
  limitations: string[];
  references: string[];
  documentation: EvaluationMethodDocumentation;
};

export type DataQualityIssue = {
  severity: 'info' | 'warning' | 'error';
  code: string;
  message: string;
  parameterId?: string;
};

export type DataQualityReport = {
  pointCount: number;
  finiteDepthCount: number;
  availableIndicatorIds: string[];
  missingIndicatorIds: string[];
  rqAvailable: boolean;
  cqAvailable: boolean;
  completeness: number;
  canCalculate: boolean;
  canSaveFormal: boolean;
  canActivate: boolean;
  issues: DataQualityIssue[];
};

export type EvaluationOutput = {
  points: SweetSpotPointResult[];
  ranges: ParameterRanges;
  quality: DataQualityReport;
};

export type EvaluationRunStatus = 'draft' | 'saved' | 'syncFailed' | 'active';

export type EvaluationRun = {
  runId: string;
  projectId: number;
  wellId: string;
  methodKey: EvaluationMethodKey;
  methodId: EvaluationMethodId;
  methodVersion: '1.0.0';
  definitionHash: string;
  methodName: string;
  scopeLabel: string;
  status: EvaluationRunStatus;
  createdAt: string;
  updatedAt: string;
  points: SweetSpotPointResult[];
  config: EvaluationMethodConfig;
  quality: DataQualityReport;
  inputSnapshot: {
    pointCount: number;
    intervalTop: number | null;
    intervalBottom: number | null;
    parameterRanges: ParameterRanges;
    sourceCurveIds: string[];
    /** Optional signatures preserve compatibility with older IndexedDB runs. */
    inputSignature?: string;
    configSignature?: string;
  };
  combinationFunction: CombinationStrategy;
  syncError?: string;
  activatedAt?: string;
};

export type MethodEvaluator = (
  points: SweetSpotInputPoint[],
  config: EvaluationMethodConfig,
  definition: EvaluationMethodDefinition,
) => EvaluationOutput;

type RegisteredMethod = { definition: EvaluationMethodDefinition; evaluate: MethodEvaluator };
const registry = new Map<EvaluationMethodKey, RegisteredMethod>();

export function registerEvaluationMethod(method: RegisteredMethod): void {
  const key = `${method.definition.methodId}@${method.definition.version}` as EvaluationMethodKey;
  const existing = registry.get(key);
  if (existing?.definition.definitionHash === method.definition.definitionHash) return;
  if (existing) throw new Error(`评价方法版本冲突：${key}`);
  registry.set(key, method);
}

export function getEvaluationMethod(key: EvaluationMethodKey): RegisteredMethod {
  const method = registry.get(key);
  if (!method) throw new Error(`未注册的评价方法：${key}`);
  return method;
}

export function listEvaluationMethods(): EvaluationMethodDefinition[] {
  return [...registry.values()].map(({ definition }) => definition);
}

export function evaluateWithMethod(
  key: EvaluationMethodKey,
  points: SweetSpotInputPoint[],
  config: EvaluationMethodConfig,
): EvaluationOutput {
  const method = getEvaluationMethod(key);
  return method.evaluate(points, config, method.definition);
}

export function stableDefinitionHash(value: unknown): string {
  const source = JSON.stringify(value);
  let hash = 0x811c9dc5;
  for (let index = 0; index < source.length; index += 1) {
    hash ^= source.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return `fnv1a32:${(hash >>> 0).toString(16).padStart(8, '0')}`;
}

export function buildEvaluationInputSignature(
  points: SweetSpotInputPoint[],
  sourceCurveIds: string[] = [],
): string {
  return stableDefinitionHash({
    sourceCurveIds: [...new Set(sourceCurveIds.filter(Boolean))].sort(),
    points: points.map((point) => ({
      depth: isFiniteNumber(point.depth) ? point.depth : null,
      values: Object.fromEntries(Object.entries(point.values)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, value]) => [key, isFiniteNumber(value) ? value : null])),
    })),
  });
}

export function buildEvaluationConfigSignature(config: EvaluationMethodConfig): string {
  // 审核信息和未启用的自定义权重快照不属于计算输入，避免纯界面切换使结果标记为待更新。
  const { reviewState, reviewComment, customWeights, ...calculationConfig } = config;
  return stableDefinitionHash(calculationConfig);
}

export function classifyByThresholds(
  score: number | null | undefined,
  thresholds: GradeThresholds,
): GradeKey | null {
  const value = clamp01(score);
  if (value == null) return null;
  if (value >= thresholds.classI) return 'I';
  if (value >= thresholds.classII) return 'II';
  if (value >= thresholds.classIII) return 'III';
  if (value >= thresholds.classIV) return 'IV';
  return 'V';
}

export function validateThresholds(thresholds: GradeThresholds): boolean {
  return [thresholds.classI, thresholds.classII, thresholds.classIII, thresholds.classIV]
    .every((value) => isFiniteNumber(value) && value >= 0 && value <= 1)
    && thresholds.classI > thresholds.classII
    && thresholds.classII > thresholds.classIII
    && thresholds.classIII > thresholds.classIV;
}

function interpolatePiecewise(value: number, breakpoints: PiecewiseAnchor[]): number | null {
  const points = breakpoints
    .filter((item) => isFiniteNumber(item.value) && isFiniteNumber(item.score))
    .slice().sort((a, b) => a.value - b.value);
  if (points.length < 2) return null;
  if (value <= points[0].value) return clamp01(points[0].score);
  if (value >= points[points.length - 1].value) return clamp01(points[points.length - 1].score);
  for (let index = 1; index < points.length; index += 1) {
    const right = points[index];
    if (value <= right.value) {
      const left = points[index - 1];
      if (right.value === left.value) return clamp01((left.score + right.score) / 2);
      const ratio = (value - left.value) / (right.value - left.value);
      return clamp01(left.score + ratio * (right.score - left.score));
    }
  }
  return null;
}

export function scoreByPhysicalAnchor(
  value: number | null | undefined,
  anchor: PhysicalAnchor | undefined,
): number | null {
  if (!isFiniteNumber(value) || !anchor?.confirmed) return null;
  if (anchor.scoringType === 'piecewise_linear') {
    return interpolatePiecewise(value, anchor.breakpoints ?? []);
  }
  if (anchor.scoringType === 'target_range') {
    if (![anchor.outerMin, anchor.outerMax, anchor.targetMin, anchor.targetMax].every(isFiniteNumber)) return null;
    return normalizeTargetRange(
      value,
      { min: anchor.outerMin!, max: anchor.outerMax! },
      anchor.targetMin,
      anchor.targetMax,
    );
  }
  if (!isFiniteNumber(anchor.badValue) || !isFiniteNumber(anchor.goodValue)) return null;
  const min = Math.min(anchor.badValue, anchor.goodValue);
  const max = Math.max(anchor.badValue, anchor.goodValue);
  const direction: ParameterDirection = anchor.scoringType === 'negative_linear'
    ? 'smaller_is_better' : 'larger_is_better';
  return normalizeMinMax(value, min, max, direction);
}

function effectiveWeights(
  available: SweetSpotParameter[],
  group: ParameterGroup,
  config: EvaluationMethodConfig,
): Record<string, number> {
  if (config.weightStrategy !== 'ahp') return normalizeWeights(available);
  const allEnabled = config.parameters[group].filter((parameter) => parameter.enabled);
  const ahp = config.ahp?.[group];
  if (!ahp?.consistent || ahp.weights.length !== allEnabled.length) return normalizeWeights(available);
  const raw = available.map((parameter) => {
    const index = allEnabled.findIndex((item) => item.id === parameter.id);
    return index < 0 ? 0 : ahp.weights[index];
  });
  const total = raw.reduce((sum, weight) => sum + weight, 0);
  return Object.fromEntries(available.map((parameter, index) => [
    parameter.id, total > 0 ? raw[index] / total : 1 / available.length,
  ]));
}

export function calculateRelativeGroupScore(
  parameters: SweetSpotParameter[],
  group: ParameterGroup,
  values: ParameterValueMap,
  ranges: ParameterRanges,
  config: EvaluationMethodConfig,
): number | null {
  const available = parameters.filter((parameter) => parameter.enabled
    && ranges[parameter.key] && isFiniteNumber(values[parameter.key]));
  if (!available.length) return null;
  const weights = effectiveWeights(available, group, config);
  const score = available.reduce((sum, parameter) => {
    const range = ranges[parameter.key]!;
    const normalized = parameter.direction === 'target_range'
      ? normalizeTargetRange(values[parameter.key]!, range, parameter.targetMin, parameter.targetMax)
      : normalizeMinMax(values[parameter.key], range.min, range.max, parameter.direction);
    return normalized == null ? sum : sum + normalized * (weights[parameter.id] ?? 0);
  }, 0);
  return clamp01(score);
}

export function calculateAnchoredGroupScore(
  parameters: SweetSpotParameter[],
  group: ParameterGroup,
  values: ParameterValueMap,
  config: EvaluationMethodConfig,
): number | null {
  const available = parameters.filter((parameter) => parameter.enabled
    && isFiniteNumber(values[parameter.key])
    && scoreByPhysicalAnchor(values[parameter.key], config.anchors[parameter.id]) != null);
  if (!available.length) return null;
  const weights = effectiveWeights(available, group, config);
  return clamp01(available.reduce((sum, parameter) => (
    sum + scoreByPhysicalAnchor(values[parameter.key], config.anchors[parameter.id])! * (weights[parameter.id] ?? 0)
  ), 0));
}

export function combineScores(
  rq: number | null,
  cq: number | null,
  strategy: CombinationStrategy,
  allowSingleGroupFallback: boolean,
): number | null {
  if (isFiniteNumber(rq) && isFiniteNumber(cq)) {
    return strategy === 'geometric_mean'
      ? clamp01(Math.sqrt(Math.max(0, rq) * Math.max(0, cq)))
      : clamp01(0.5 * rq + 0.5 * cq);
  }
  if (!allowSingleGroupFallback) return null;
  if (isFiniteNumber(rq)) return clamp01(rq);
  if (isFiniteNumber(cq)) return clamp01(cq);
  return null;
}

export function assessDataQuality(
  points: SweetSpotInputPoint[],
  config: EvaluationMethodConfig,
  _definition: EvaluationMethodDefinition,
): DataQualityReport {
  const enabled = [...config.parameters.rq, ...config.parameters.cq].filter((parameter) => parameter.enabled);
  const available = enabled.filter((parameter) => points.some((point) => isFiniteNumber(point.values[parameter.key])));
  const availableIds = available.map((parameter) => parameter.id);
  const missingParameters = enabled.filter((parameter) => !availableIds.includes(parameter.id));
  const missingIds = missingParameters.map((parameter) => parameter.id);
  const rqAvailable = config.parameters.rq.some((parameter) => availableIds.includes(parameter.id));
  const cqAvailable = config.parameters.cq.some((parameter) => availableIds.includes(parameter.id));
  const issues: DataQualityIssue[] = [];
  if (!points.length) issues.push({ severity: 'error', code: 'EMPTY_DATA', message: '当前井段没有可评价数据。' });
  missingParameters.forEach((parameter) => issues.push({
    severity: 'warning', code: 'MISSING_INDICATOR', parameterId: parameter.id,
    message: `缺失参数：${parameter.label}。`,
  }));
  if (!rqAvailable) issues.push({ severity: 'error', code: 'RQ_INSUFFICIENT', message: '缺少储层品质（RQ）评价参数。' });
  if (!cqAvailable) issues.push({ severity: 'error', code: 'CQ_INSUFFICIENT', message: '缺少工程品质（CQ）评价参数。' });

  const ahpInvalid = config.weightStrategy === 'ahp' && (['rq', 'cq'] as const).some((group) => {
    const count = config.parameters[group].filter((parameter) => parameter.enabled).length;
    return count > 0 && (!config.ahp?.[group]?.consistent || config.ahp[group]!.weights.length !== count);
  });
  if (ahpInvalid) issues.push({
    severity: 'error', code: 'AHP_INCONSISTENT',
    message: 'AHP判断矩阵未通过一致性检验（CR 必须小于 0.1），不能确认使用该权重。',
  });
  if (!validateThresholds(config.gradeThresholds)) issues.push({
    severity: 'error', code: 'INVALID_THRESHOLDS', message: 'Ⅰ～Ⅴ级阈值必须位于0～1且严格递减。',
  });
  const formalErrors = issues.some((issue) => issue.severity === 'error'
    && !['RQ_INSUFFICIENT', 'CQ_INSUFFICIENT'].includes(issue.code));
  const hasAnyGroup = rqAvailable || cqAvailable;
  const dualComplete = rqAvailable && cqAvailable;
  const canCalculate = points.length > 0 && hasAnyGroup;
  const canSaveFormal = canCalculate && !formalErrors && hasAnyGroup;
  const canActivate = canSaveFormal && (dualComplete
    || config.allowLegacySingleGroupActivation);
  return {
    pointCount: points.length,
    finiteDepthCount: points.filter((point) => isFiniteNumber(point.depth)).length,
    availableIndicatorIds: availableIds,
    missingIndicatorIds: missingIds,
    rqAvailable,
    cqAvailable,
    completeness: enabled.length ? available.length / enabled.length : 0,
    canCalculate,
    canSaveFormal,
    canActivate,
    issues,
  };
}

const RANDOM_INDEX: Record<number, number> = {
  1: 0, 2: 0, 3: 0.58, 4: 0.90, 5: 1.12, 6: 1.24, 7: 1.32, 8: 1.41, 9: 1.45,
};

export function calculateAHP(matrix: number[][]): AHPResult {
  const size = matrix.length;
  const warnings: string[] = [];
  if (!size || matrix.some((row) => row.length !== size)) throw new Error('AHP判断矩阵必须为非空方阵。');
  matrix.forEach((row, rowIndex) => row.forEach((value, columnIndex) => {
    if (!isFiniteNumber(value) || value <= 0) throw new Error('AHP判断矩阵元素必须为正数。');
    if (rowIndex === columnIndex && Math.abs(value - 1) > 1e-6) warnings.push('判断矩阵对角线应为1。');
    const reciprocal = matrix[columnIndex]?.[rowIndex];
    if (isFiniteNumber(reciprocal) && Math.abs(value * reciprocal - 1) > 1e-3) warnings.push('判断矩阵存在非互反元素。');
  }));
  if (size > 9) warnings.push('单层指标超过9个，建议拆分层级后重新判断。');
  let weights = Array.from({ length: size }, () => 1 / size);
  for (let iteration = 0; iteration < 1000; iteration += 1) {
    const next = matrix.map((row) => row.reduce((sum, value, index) => sum + value * weights[index], 0));
    const total = next.reduce((sum, value) => sum + value, 0);
    const normalized = next.map((value) => value / total);
    const delta = normalized.reduce((max, value, index) => Math.max(max, Math.abs(value - weights[index])), 0);
    weights = normalized;
    if (delta < 1e-12) break;
  }
  const multiplied = matrix.map((row) => row.reduce((sum, value, index) => sum + value * weights[index], 0));
  const lambdaMax = multiplied.reduce((sum, value, index) => sum + value / weights[index], 0) / size;
  const ci = size <= 2 ? 0 : (lambdaMax - size) / (size - 1);
  const randomIndex = RANDOM_INDEX[size];
  const cr = size <= 2 ? 0 : randomIndex ? ci / randomIndex : Number.POSITIVE_INFINITY;
  return {
    matrix: matrix.map((row) => [...row]),
    weights,
    lambdaMax,
    ci,
    cr,
    consistent: cr < 0.1 && !warnings.some((warning) => warning.includes('非互反')),
    warnings: [...new Set(warnings)],
  };
}

export function createRunId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `run-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function createEvaluationRun(options: {
  projectId: number;
  wellId: string;
  methodKey: EvaluationMethodKey;
  config: EvaluationMethodConfig;
  output: EvaluationOutput;
  sourceCurveIds: string[];
  inputSignature?: string;
}): EvaluationRun {
  const method = getEvaluationMethod(options.methodKey).definition;
  const depths = options.output.points.map((point) => point.depth).filter(isFiniteNumber).sort((a, b) => a - b);
  const now = new Date().toISOString();
  return {
    runId: createRunId(),
    projectId: options.projectId,
    wellId: options.wellId,
    methodKey: options.methodKey,
    methodId: method.methodId,
    methodVersion: method.version,
    definitionHash: method.definitionHash,
    methodName: method.name,
    scopeLabel: method.scopeLabel,
    status: 'draft',
    createdAt: now,
    updatedAt: now,
    points: options.output.points,
    config: options.config,
    quality: options.output.quality,
    inputSnapshot: {
      pointCount: options.output.points.length,
      intervalTop: depths[0] ?? null,
      intervalBottom: depths[depths.length - 1] ?? null,
      parameterRanges: options.output.ranges,
      sourceCurveIds: [...new Set(options.sourceCurveIds.filter(Boolean))],
      inputSignature: options.inputSignature,
      configSignature: buildEvaluationConfigSignature(options.config),
    },
    combinationFunction: options.config.combination,
  };
}

export function evaluateRelativeMethod(
  points: SweetSpotInputPoint[],
  config: EvaluationMethodConfig,
  definition: EvaluationMethodDefinition,
  allowSingleGroupFallback: boolean,
): EvaluationOutput {
  const ranges = buildParameterRanges(points);
  const quality = assessDataQuality(points, config, definition);
  const results = points.map((point) => {
    const rq = calculateRelativeGroupScore(config.parameters.rq, 'rq', point.values, ranges, config);
    const cq = calculateRelativeGroupScore(config.parameters.cq, 'cq', point.values, ranges, config);
    const ssi = combineScores(rq, cq, config.combination, allowSingleGroupFallback);
    return { depth: point.depth, rq, cq, ssi, grade: classifyByThresholds(ssi, config.gradeThresholds) };
  });
  return { points: results, ranges, quality };
}

export function evaluateAnchoredMethod(
  points: SweetSpotInputPoint[],
  config: EvaluationMethodConfig,
  definition: EvaluationMethodDefinition,
): EvaluationOutput {
  const ranges = buildParameterRanges(points);
  const quality = assessDataQuality(points, config, definition);
  const results = points.map((point) => {
    const rq = calculateAnchoredGroupScore(config.parameters.rq, 'rq', point.values, config);
    const cq = calculateAnchoredGroupScore(config.parameters.cq, 'cq', point.values, config);
    const ssi = combineScores(rq, cq, config.combination, false);
    return { depth: point.depth, rq, cq, ssi, grade: classifyByThresholds(ssi, config.gradeThresholds) };
  });
  return { points: results, ranges, quality };
}
